"use client";
import React, { useState, useEffect, useContext } from "react";
import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";

import withExtendedStyles from "@sendmagic/prismic-nextjs/src/hoc/withExtendedStyles";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import PrismicImgix from "@sendmagic/prismic-nextjs/src/components/Imgix";
import { ModalVideo } from "@sendmagic/prismic-nextjs/src/components/utils/ModalVideo";
import { textResolver } from "@sendmagic/prismic-nextjs/src/constants/text-resolver";
import { VideoPlayer } from "@stadium/ui/src/components/video-player";

import { checkContentPresence } from "@sendmagic/prismic-nextjs/src/helpers/prismic";
import { fetchWistiaVideo, getWistiaPlayerLink, getWistiaVideoId } from "@sendmagic/prismic-nextjs/src/helpers/videos";

import defaultStyles from "./main.module.scss";

const VideoSlice = ({ doc, sliceStyles: styles = defaultStyles }) => {
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const {
    heading,
    description,
    button_text,
    image,
    wistia_video_id,
    internal_link_id,
    video,
    image_alignment,
  } = doc?.primary || {};
  const wistiaId = wistia_video_id || getWistiaVideoId(video);
  const alignmentStyle = styles[`${image_alignment}VideoColumn`];

  useEffect(() => {
    if (wistiaId) {
      fetchWistiaVideo(wistiaId);
    }
  }, [wistiaId]);

  const closeModal = () => {
    setOpenVideoModal(false);
  };

  const openModal = () => {
    setOpenVideoModal(true);
  };

  const renderFallbackMedia = () => (
    <>
      {wistiaId ? (
        <div>
          <div className={styles.videoHolder}>
            <Multimedia
              type="iframe"
              src={getWistiaPlayerLink(wistiaId)}
              className={styles.multimedia}
            />
          </div>
        </div>
      ) : (
        <PrismicImgix
          alt={image?.alt}
          src={image?.url}
          className={styles.imgixClass}
          desktop="850px"
          mobile="100%"
          tablet="400px"
          largeDisplay="1600px"
        />
      )}
    </>
  );

  return (
    <div className={`${styles.videoSliceSection} ${alignmentStyle}`}>
      {internal_link_id && <span id={internal_link_id} className={styles.linkId} />}
      {openVideoModal && <ModalVideo video={video} closeModal={closeModal} />}
      <div className={styles.videoContentWrapper}>
        <div className={styles.videoContentBlock}>
          <div className={styles.videoSectionContentHolder}>
            {checkContentPresence(heading) && (
              <div className={styles.videoSectionTitle}>
                {textResolver(heading, { convertLabelToModular: true })}
              </div>
            )}
            {checkContentPresence(description) && (
              <div className={styles.videoSectionDescription}>
                {textResolver(description, { convertLabelToModular: true })}
              </div>
            )}
            <div className={styles.videoContentList}>
              {doc?.items?.map(
                ({ text }, index) =>
                  checkContentPresence(text) && (
                    <div className={styles.videoContentListItem} key={`sgg-li${index}`}>
                      <div className={styles.contentCountContainer}>
                        <span className={styles.videoContentCount}>{index + 1}</span>
                      </div>
                      <div className={styles.videoContentText}>
                        {textResolver(text, { convertLabelToModular: true })}
                      </div>
                    </div>
                  ),
              )}

              {wistiaId
                ? checkContentPresence(button_text) && (
                    <div
                      className={`${styles.wistiaWrapper} wistia_embed wistia_async_${wistiaId} popover=true popoverAnimateThumbnail=true popoverContent=link`}
                    >
                      <a className={`${styles.btnMarketing} ${styles.wistiaBtnTextWrapper}`}>
                        {textResolver(button_text, { convertLabelToModular: true })}
                      </a>
                    </div>
                  )
                : video &&
                  checkContentPresence(button_text) && (
                    <a
                      onClick={openModal}
                      className={`${styles.btnMarketing} ${styles.videoBtnTextWrapper} `}
                    >
                      {textResolver(button_text, { convertLabelToModular: true })}
                    </a>
                  )}
            </div>
          </div>
        </div>
        <div className={styles.videoImgSection}>
          {video?.embed_url ? (
            <div>
              <VideoPlayer
                className={styles.videoHolder}
                url={video.embed_url}
                height={isMobileDevice ? "280px" : "590px"}
                width="100%"
              />
            </div>
          ) : (
            renderFallbackMedia()
          )}
        </div>
      </div>
    </div>
  );
};

export default withExtendedStyles(VideoSlice, ({ appName }) => ({
  styleImport: () => {
    switch (appName) {
      case "swagmagic":
        return import(
          /* webpackChunkName: "PriVideoSliceSwagmagicStyles" */ "./swagmagic.module.scss"
        );
      default:
        return import(/* webpackChunkName: "PriVideoSlicedefaultStyles" */ "./main.module.scss");
    }
  },
}));
